import React, { useEffect, useState } from "react"
import tw from "twin.macro"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons"
import { usePartnerFilterContext } from "../../contexts/PartnerFilterContext"
import { PartnerGridStoryblok } from "../../../component-types-sb"
import DropdownInput from "../general/DropdownInput"
import ButtonFilterSection from "../general/ButtonFilterSection"


const ClearButton = tw.div`
  mb-4 w-full relative
`

const Label = tw.label`
  block mb-2
  text-blue-prussian
  text-26
`

const TagButton = tw.button`
  p-2 rounded-xl border border-blue-cerulean
  m-1
  bg-blue-sky text-blue-prussian
  aria-pressed:(bg-blue-cerulean text-blue-sky border-blue-cerulean)
`

const FilterWrapper = tw.div`
  w-full
`

const MobileFilterButton = tw.button`
  flex justify-between items-center
  w-full py-2 px-4
  border border-blue-cerulean
  rounded
  text-blue-prussian
  laptop:hidden
  [&[data-filter-open="true"]]:(
    rounded-b-none
  )
`

const FilterContent = tw.div`
  hidden
  [&[data-filter-open="true"]]:block
  border border-blue-cerulean
  rounded-b
  p-4
  laptop:(block border-0 rounded-none p-0)
`

interface Props {
  readonly blok: PartnerGridStoryblok
}

const PartnerTagFilter: React.FC<Props> = ({ blok }) => {
  const {
    searchFields,
    setSearchFields,
    selectedFilters,
    handleFilterChange,
    filteredProvinces,
    filteredCities,
    filterOptions
  } = usePartnerFilterContext()
  const [filterOpen, setFilterOpen] = useState(false)
  const isAnyFilterActive =
    selectedFilters.country !== "" ||
    selectedFilters.province !== "" ||
    selectedFilters.city !== "" ||
    selectedFilters.studioAmount !== "" ||
    selectedFilters.type.length > 0 ||
    selectedFilters.extra.length > 0

  useEffect(() => {
    setSearchFields({
      citySearch: selectedFilters.city,
      countrySearch: selectedFilters.country,
      provinceSearch: selectedFilters.province,
    })
  }, [selectedFilters, setSearchFields])

  const handleLocationSelect = (type: "country" | "province" | "city", value: string | null) => {
    const updatedFilters: Partial<typeof selectedFilters> = {}

    if (type === "country") {
      updatedFilters.country = value || ""
      updatedFilters.province = ""
      updatedFilters.city = ""
    } else if (type === "province") {
      updatedFilters.province = value || ""
      updatedFilters.city = ""
    } else if (type === "city") {
      updatedFilters.city = value || ""
    }

    handleFilterChange(updatedFilters)
    setSearchFields((prev) => ({
      ...prev,
      [`${type}Search`]: value || "",
      ...(type === "country" && { citySearch: "", provinceSearch: "" }),
      ...(type === "province" && { citySearch: "" }),
    }))
  }

  const handleClearAllFilters = () => {
    handleFilterChange({
      city: "",
      country: "",
      extra: [],
      province: "",
      studioAmount: "",
      type: [],
    })
    setSearchFields({
      citySearch: "",
      countrySearch: "",
      provinceSearch: "",
    })
  }

  return (
    <FilterWrapper>
      {/* Toggle Button */}
      <MobileFilterButton
        data-filter-open={filterOpen}
        onClick={() => setFilterOpen(!filterOpen)}
      >
      {filterOpen
        ? blok.hide_filters_text
        : blok.show_filters_text
      }
      {filterOpen
        ? <FontAwesomeIcon icon={faChevronUp} />
        : <FontAwesomeIcon icon={faChevronDown} />
      }
      </MobileFilterButton>
      {/* Filter Content */}
      <FilterContent data-filter-open={filterOpen}>
        {/* Country Input with Search */}
        <Label>{blok.location_filter_title}</Label>
        <DropdownInput
          options={filterOptions.countries.map((country) => ({ label: country, value: country }))}
          value={
            searchFields.countrySearch
              ? { label: searchFields.countrySearch, value: searchFields.countrySearch }
              : null
          }
          onChange={(selectedOption) =>
            handleLocationSelect("country", selectedOption?.value || null)
          }
          isClearable
          placeholder={blok.country_title}
        />
        <DropdownInput
          options={filteredProvinces.map((province) => ({ label: province, value: province }))}
          value={
            searchFields.provinceSearch
              ? { label: searchFields.provinceSearch, value: searchFields.provinceSearch }
              : null
          }
          onChange={(selectedOption) =>
            handleLocationSelect("province", selectedOption?.value || null)
          }
          isClearable
          placeholder={blok.province_title}
        />
        <DropdownInput
          options={filteredCities.map((city) => ({ label: city, value: city }))}
          value={
            searchFields.citySearch
              ? { label: searchFields.citySearch, value: searchFields.citySearch }
              : null
          }
          onChange={(selectedOption) =>
            handleLocationSelect("city", selectedOption?.value || null)
          }
          isClearable
          placeholder={blok.city_title}
        />
        {/* Type options */}
        <ButtonFilterSection
          label={blok.type_filter_title}
          options={filterOptions.types}
          selectedValues={selectedFilters.type}
          onChange={(value) => {
            handleFilterChange({ type: value as string[] })
          }}
          isMultiSelect
        />
        {/* Studios amount */}
        <ButtonFilterSection
          label={blok.studios_filter_title}
          options={filterOptions.studioAmounts}
          selectedValues={selectedFilters.studioAmount}
          onChange={(value) => {
            handleFilterChange({ studioAmount: value as string })
          }}
          isSmall={true}
        />
        {/* Extra options */}
        <ButtonFilterSection
          label={blok.extra_filter_title}
          options={filterOptions.extras}
          selectedValues={selectedFilters.extra}
          onChange={(value) => {
            handleFilterChange({ extra: value as string[] })
          }}
          isMultiSelect
        />
        {isAnyFilterActive && (
          <ClearButton>
            <TagButton
              type="button"
              onClick={handleClearAllFilters}
              tw="w-full bg-blue-cerulean text-blue-sky"
            >
              {blok.clear_filters_text}
            </TagButton>
          </ClearButton>
        )}
      </FilterContent>
    </FilterWrapper>
  )
}

export default PartnerTagFilter
